import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const MIN_LEN = 8;

const FeedbackModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [liked, setLiked] = useState(null);
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
  const textareaRef = useRef(null);

  const readyToSend = token && message.trim().length >= MIN_LEN && !status.loading;

  // Capture context and fetch token on open
  useEffect(() => {
    const c = {
      from_path: window.location.pathname,
      from_url: window.location.href,
      from_title: document.title,
      ts: Date.now()
    };
    setCtx(c);

    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/api/feedback-token", {
          page_path: c.from_path,
          page_title: c.from_title
        });
        if (!cancelled && res.data?.token) {
          setToken(res.data.token);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus(s => ({ ...s, error: err?.response?.data?.error || err.message || "Token error" }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Autofocus textarea
  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  // Close on ESC
  useEffect(() => {
    const handler = e => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const openFullPage = () => {
    if (ctx) {
      sessionStorage.setItem("ccg_feedback_context", JSON.stringify(ctx));
    }
    if (token) {
      sessionStorage.setItem("ccg_feedback_token", token);
    }
    navigate("/feedback");
  };

  const submit = async e => {
    e.preventDefault();
    if (!readyToSend) return;
    setStatus({ loading: true, ok: false, error: "" });

    try {
      await api.post("/api/feedback", {
        token: token || null,
        name: name.trim() || null,
        email: email.trim() || null,
        liked,
        message: message.trim(),
        honeypot,
        page_path: ctx?.from_path || null,
        page_url: ctx?.from_url || null,
        page_title: ctx?.from_title || null,
        client_ts: ctx?.ts || null
      });
      setStatus({ loading: false, ok: true, error: "" });
      setMessage("");
      setName("");
      setEmail("");
      setLiked(null);
      sessionStorage.removeItem("ccg_feedback_context");
      sessionStorage.removeItem("ccg_feedback_token");
      // auto-close after short delay
      setTimeout(() => {
        onClose();
      }, 5000);
    } catch (err) {
      const apiError =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to submit feedback";
      setStatus({ loading: false, ok: false, error: apiError });
    }
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h3 style={{ margin: 0 }}>Quick feedback</h3>
            <p style={{ margin: "4px 0 0", color: "#4b5563", fontSize: 13 }}>
              Was this page helpful? What should we improve?
            </p>
          </div>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close feedback">
            ×
          </button>
        </div>

        {ctx?.from_path && (
          <div style={styles.contextBox}>
            <div style={{ fontSize: 13, color: "#111" }}>
              <strong>Page:</strong> {ctx.from_path}
            </div>
            <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{ctx.from_title}</div>
          </div>
        )}

        <form onSubmit={submit}>
          <input
            type="text"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
          />

          <div style={{ marginBottom: 10 }}>
            <label style={styles.label}>Did this page help?</label>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setLiked(true)} style={pill(liked === true)}>
                👍 Yes
              </button>
              <button type="button" onClick={() => setLiked(false)} style={pill(liked === false)}>
                👎 Not really
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={styles.label}>What should we improve? (required)</label>
            <textarea
              ref={textareaRef}
              style={{ ...styles.input, minHeight: 110 }}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Example: Train fare seems off, show Reserve fees, unclear steps, etc."
              required
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <div>
              <label style={styles.label}>Name (optional)</label>
              <input style={styles.input} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Email (optional)</label>
              <input style={styles.input} value={email} onChange={e => setEmail(e.target.value)} />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Only if you want a reply.</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
            <button
              type="submit"
              disabled={!readyToSend}
              style={{
                ...styles.submitBtn,
                opacity: readyToSend ? 1 : 0.5,
                cursor: readyToSend ? "pointer" : "not-allowed"
              }}
            >
              {status.loading ? "Sending..." : "Send feedback"}
            </button>
            {status.ok && (
              <span style={{ color: "#14532d", fontSize: 13, fontWeight: 700 }}>
                Thanks — we read every message. Returning to your page…
              </span>
            )}
            {status.error && <span style={{ color: "crimson", fontSize: 13 }}>{status.error}</span>}
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Prefer a full page?{" "}
            <button type="button" onClick={openFullPage} style={styles.linkBtn}>
              Open feedback page →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    zIndex: 1000
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    padding: 16,
    maxWidth: 520,
    width: "100%",
    boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
    border: "1px solid #e5e7eb"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10
  },
  closeBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 8,
    width: 30,
    height: 30,
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1
  },
  contextBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: 10,
    marginBottom: 10
  },
  label: { display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6 },
  input: { width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd", fontSize: 14 },
  submitBtn: {
    background: "#f4c400",
    border: "none",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 800
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#0ea5e9",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline"
  }
};

const pill = active => ({
  border: "1px solid #ddd",
  background: active ? "#eef9f0" : "#fff",
  padding: "8px 12px",
  borderRadius: 999,
  cursor: "pointer",
  fontWeight: 700
});

export default FeedbackModal;
